import { queryOptions } from "@tanstack/react-query"
import { api } from "./api"
import {
  compareWallClockToNow,
  differenceWallClockDays,
  formatWallClockDate,
  formatWallClockDateTime,
  formatWallClockTime,
  wallClockDayKey,
} from "./local-datetime"

// Travel manifest data layer for the Console. Mirrors apps/admin's TravelPage
// read shape: one POST round-trip returns the event's flights, ground
// transfers, lodging, rentals + unscheduled bookings, each with its passenger
// list. Read-only v1 — the bespoke canvas flattens these into time-ordered
// lane rows and a passenger manifest.

export type GuestSummary = { id: string; name: string; email?: string | null }

export type RoomEntry = {
  reservation: {
    id: string
    hotelName: string
    addressLocality: string | null
    addressRegion: string | null
    checkInAt: string | null
    checkOutAt: string | null
    roomType: string | null
    bedConfiguration: string | null
    numAdults: number | null
    numChildren: number | null
    confirmationNumber: string | null
  }
  occupants: Array<{ guest: GuestSummary; bookingId: string | null }>
}

export type PnrSegment = {
  id: string
  position: number
  carrierIataCode: string | null
  carrierName: string | null
  flightNumber: string | null
  departureAirportCode: string
  departureCity: string | null
  departureAt: string
  arrivalAirportCode: string
  arrivalCity: string | null
  arrivalAt: string
  seats: Array<{ guest: GuestSummary; seat: string }>
}

export type PnrEntry = {
  reservation: {
    id: string
    direction: string
    pnr: string | null
    airlineName: string | null
    airlineIataCode: string | null
    status?: string | null
  }
  segments: PnrSegment[]
  passengers: Array<{
    guest: GuestSummary
    ticketNumber: string | null
    bookingId: string | null
  }>
}

export type StandaloneBookingEntry = {
  booking: {
    id: string
    bookingSource: string
    agencyName: string | null
    bookingCode: string | null
    notes: string | null
  }
  guests: GuestSummary[]
}

export type TransferEntry = {
  transfer: {
    id: string
    direction: string
    mode: string
    transferType: string
    status?: string | null
    vendorName: string
    vendorContact: string | null
    confirmationNumber: string | null
    serviceNumber: string | null
    serviceClass: string | null
    pickupAt: string
    pickupLocationId: string | null
    pickupLocationName: string | null
    pickupAddressLocality: string | null
    pickupStationCode: string | null
    pickupPlatform: string | null
    dropoffAt: string
    dropoffLocationId: string | null
    dropoffLocationName: string | null
    dropoffAddressLocality: string | null
    dropoffStationCode: string | null
    dropoffPlatform: string | null
    passengerCount: number | null
    luggageCount: number | null
    notes: string | null
  }
  passengers: Array<{
    guest: GuestSummary
    seat: string | null
    ticketNumber: string | null
    bookingId: string | null
  }>
}

export type RentalEntry = {
  rental: {
    id: string
    vendorName: string
    confirmationNumber: string | null
    vehicleClass: string | null
    vehicleDescription: string | null
    vehicleMake: string | null
    vehicleModel: string | null
    licensePlate: string | null
    pickupAt: string
    pickupLocationName: string | null
    pickupAddressLocality: string | null
    dropoffAt: string
    dropoffLocationName: string | null
    dropoffAddressLocality: string | null
    notes: string | null
  }
  occupants: Array<{
    guest: GuestSummary
    role: string
    bookingId: string | null
  }>
}

export type EventTravelResponse = {
  success: boolean
  rooms: RoomEntry[]
  pnrs: PnrEntry[]
  transfers: TransferEntry[]
  rentals: RentalEntry[]
  standaloneBookings: StandaloneBookingEntry[]
}

export const eventTravelKey = (eventId: string) =>
  ["resource", "event-travel", { eventId }] as const

export function eventTravelQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: eventTravelKey(eventId),
    queryFn: () =>
      api<EventTravelResponse>("/api/v1/admin/event-travel", {
        method: "POST",
        body: { eventId },
      }),
  })
}

// ── manifest model ───────────────────────────────────────────────────────────

export type Lane = "flights" | "ground" | "lodging" | "rentals" | "unscheduled"

export type PassengerDetail = {
  id: string
  name: string
  email?: string | null
  seat?: string | null
  ticketNumber?: string | null
  role?: string | null
  reference?: string | null
}

export type ManifestItem = {
  id: string
  kind: "flight" | "transfer" | "lodging" | "rental" | "booking"
  lane: Lane
  /** Arriving vs departing, for the flights lane split. */
  direction: "arriving" | "departing" | null
  startsAt: string | null
  endsAt: string | null
  dayKey: string | null
  title: string
  subtitle: string | null
  primaryTimeLabel: string
  secondaryTimeLabel: string | null
  meta: string[]
  passengers: PassengerDetail[]
}

const TRANSFER_TYPE_LABEL: Record<string, string> = {
  scheduled: "Scheduled",
  as_directed: "As-directed",
}

// Tolerant decode: the only meaningful value is `as_directed`; every legacy
// value (airport_to_hotel / hotel_to_airport / point_to_point) and any unknown
// collapses to `scheduled`. Keeps un-backfilled rows + old clients from
// rendering a raw enum string.
function normalizeTransferType(t: string | null | undefined): "scheduled" | "as_directed" {
  return t === "as_directed" ? "as_directed" : "scheduled"
}
function transferTypeLabel(t: string | null | undefined): string {
  return TRANSFER_TYPE_LABEL[normalizeTransferType(t)]
}
const ROLE_LABEL: Record<string, string> = {
  primary_driver: "Primary driver",
  additional_driver: "Additional driver",
  passenger: "Passenger",
}
const SOURCE_LABEL: Record<string, string> = {
  agent: "Agent",
  direct: "Direct",
  self: "Self-booked",
}

// Flatten the response into time-ordered manifest rows. One row per flight
// SEGMENT; co-travellers on an identical leg collapse into one row with an
// aggregated passenger list (matching apps/admin).
// A clean, URL-friendly, unique id for a flight movement: the departure date
// (YYYYMMDD) + airline IATA + flight number, e.g. "20270315-ua1234". Unique on
// its own — there's only one flight number per airline per day. Returns null
// when the date or flight code is missing (callers fall back to the raw key).
function flightSegmentSlug(segment: {
  departureAt?: string | null
  carrierIataCode?: string | null
  flightNumber?: string | null
}): string | null {
  const date = (wallClockDayKey(segment.departureAt) ?? "").replace(/-/g, "")
  const code = `${segment.carrierIataCode ?? ""}${segment.flightNumber ?? ""}`
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
  if (!/^\d{8}$/.test(date) || !code) return null
  return `${date}-${code}`
}

export function buildManifestItems(data: EventTravelResponse | undefined): ManifestItem[] {
  if (!data?.success) return []
  const items: ManifestItem[] = []

  const flightGroups = new Map<string, ManifestItem>()
  for (const entry of data.pnrs ?? []) {
    const segments = [...entry.segments].sort((a, b) => a.position - b.position)
    const arriving = (entry.reservation.direction ?? "").toLowerCase() === "arriving"
    const departing = (entry.reservation.direction ?? "").toLowerCase() === "departing"
    for (const segment of segments) {
      const startsAt = segment.departureAt ?? null
      const endsAt = segment.arrivalAt ?? startsAt
      const seatByGuest = new Map<string, string>()
      for (const seat of segment.seats) {
        if (!seatByGuest.has(seat.guest.id)) seatByGuest.set(seat.guest.id, seat.seat)
      }
      const flightNumber = [segment.carrierIataCode, segment.flightNumber]
        .filter(Boolean)
        .join(" ")
      const title =
        segment.departureAirportCode && segment.arrivalAirportCode
          ? `${segment.departureAirportCode} → ${segment.arrivalAirportCode}`
          : "Flight movement"
      const newPassengers: PassengerDetail[] = entry.passengers.map((passenger) => ({
        id: passenger.guest.id,
        name: passenger.guest.name,
        email: passenger.guest.email,
        seat: seatByGuest.get(passenger.guest.id) ?? null,
        ticketNumber: passenger.ticketNumber,
        reference: entry.reservation.pnr ? "Reference on guest record" : null,
      }))
      const groupKey = [
        entry.reservation.direction ?? "",
        `${segment.carrierIataCode ?? ""}${segment.flightNumber ?? ""}@${segment.departureAirportCode ?? ""}-${segment.arrivalAirportCode ?? ""}@${segment.departureAt ?? ""}>${segment.arrivalAt ?? ""}`,
      ].join("::")

      const existing = flightGroups.get(groupKey)
      if (existing) {
        const seen = new Set(existing.passengers.map((p) => p.id))
        for (const p of newPassengers) {
          if (!seen.has(p.id)) {
            existing.passengers.push(p)
            seen.add(p.id)
          }
        }
        continue
      }

      flightGroups.set(groupKey, {
        // Clean, URL-friendly id: YYYYMMDD-<airline><flight> (e.g. 20270315-ua1234).
        // Unique on its own — there's only one flight number per airline per day.
        id: flightSegmentSlug(segment) ?? `flight:${groupKey}`,
        kind: "flight",
        lane: "flights",
        direction: arriving ? "arriving" : departing ? "departing" : null,
        startsAt,
        endsAt,
        dayKey: dayKey(startsAt),
        title,
        subtitle: flightNumber || entry.reservation.airlineName,
        primaryTimeLabel: startsAt ? formatTime(startsAt) : "Time missing",
        secondaryTimeLabel: endsAt && endsAt !== startsAt ? formatTime(endsAt) : null,
        meta: [
          entry.reservation.direction ? titleCase(entry.reservation.direction) : null,
          entry.reservation.airlineName,
        ].filter(Boolean) as string[],
        passengers: newPassengers,
      })
    }
  }
  items.push(...flightGroups.values())

  for (const entry of data.transfers ?? []) {
    const pickup = transferPickupLabel(entry)
    const dropoff = transferDropoffLabel(entry)
    const startsAt = entry.transfer.pickupAt
    const endsAt = entry.transfer.dropoffAt
    items.push({
      id: `transfer:${entry.transfer.id}`,
      kind: "transfer",
      lane: "ground",
      direction: null,
      startsAt,
      endsAt,
      dayKey: dayKey(startsAt),
      title: `${pickup} → ${dropoff}`,
      subtitle: [
        entry.transfer.vendorName,
        transferTypeLabel(entry.transfer.transferType),
      ]
        .filter(Boolean)
        .join(" · "),
      primaryTimeLabel: formatTime(startsAt),
      secondaryTimeLabel: endsAt ? formatTime(endsAt) : null,
      meta: [
        titleCase(entry.transfer.mode.replaceAll("_", " ")),
        entry.transfer.serviceNumber,
        entry.transfer.passengerCount != null ? `${entry.transfer.passengerCount} pax` : null,
        entry.transfer.luggageCount != null
          ? `${entry.transfer.luggageCount} bag${entry.transfer.luggageCount === 1 ? "" : "s"}`
          : null,
      ].filter(Boolean) as string[],
      passengers: entry.passengers.map((passenger) => ({
        id: passenger.guest.id,
        name: passenger.guest.name,
        email: passenger.guest.email,
        seat: passenger.seat,
        ticketNumber: passenger.ticketNumber,
      })),
    })
  }

  for (const entry of data.rooms ?? []) {
    const startsAt = entry.reservation.checkInAt
    const endsAt = entry.reservation.checkOutAt
    const nights = nightsBetween(startsAt, endsAt)
    items.push({
      id: `lodging:${entry.reservation.id}`,
      kind: "lodging",
      lane: "lodging",
      direction: null,
      startsAt,
      endsAt,
      dayKey: dayKey(startsAt),
      title: `${entry.reservation.hotelName} check-in`,
      subtitle:
        [entry.reservation.addressLocality, entry.reservation.addressRegion]
          .filter(Boolean)
          .join(", ") || null,
      primaryTimeLabel: startsAt ? formatTime(startsAt) : "Time missing",
      secondaryTimeLabel: endsAt ? `Out ${formatDateTime(endsAt)}` : null,
      meta: [
        entry.reservation.roomType,
        entry.reservation.bedConfiguration,
        nights != null ? `${nights} night${nights === 1 ? "" : "s"}` : null,
        entry.reservation.confirmationNumber ? "Confirmation available" : null,
      ].filter(Boolean) as string[],
      passengers: entry.occupants.map((occupant) => ({
        id: occupant.guest.id,
        name: occupant.guest.name,
        email: occupant.guest.email,
      })),
    })
  }

  for (const entry of data.rentals ?? []) {
    const startsAt = entry.rental.pickupAt
    const endsAt = entry.rental.dropoffAt
    const vehicle =
      [entry.rental.vehicleMake, entry.rental.vehicleModel].filter(Boolean).join(" ") ||
      entry.rental.vehicleDescription ||
      entry.rental.vehicleClass
    items.push({
      id: `rental:${entry.rental.id}`,
      kind: "rental",
      lane: "rentals",
      direction: null,
      startsAt,
      endsAt,
      dayKey: dayKey(startsAt),
      title: `${entry.rental.pickupLocationName ?? entry.rental.pickupAddressLocality ?? "Rental"} pickup`,
      subtitle: [entry.rental.vendorName, vehicle].filter(Boolean).join(" · "),
      primaryTimeLabel: formatTime(startsAt),
      secondaryTimeLabel: endsAt ? `Return ${formatDateTime(endsAt)}` : null,
      meta: [
        entry.rental.licensePlate,
        entry.rental.confirmationNumber ? "Confirmation available" : null,
      ].filter(Boolean) as string[],
      passengers: entry.occupants.map((occupant) => ({
        id: occupant.guest.id,
        name: occupant.guest.name,
        email: occupant.guest.email,
        role: ROLE_LABEL[occupant.role] ?? occupant.role,
      })),
    })
  }

  for (const entry of data.standaloneBookings ?? []) {
    items.push({
      id: `booking:${entry.booking.id}`,
      kind: "booking",
      lane: "unscheduled",
      direction: null,
      startsAt: null,
      endsAt: null,
      dayKey: null,
      title: "Unscheduled booking",
      subtitle: [
        SOURCE_LABEL[entry.booking.bookingSource] ?? titleCase(entry.booking.bookingSource),
        entry.booking.agencyName,
      ]
        .filter(Boolean)
        .join(" · "),
      primaryTimeLabel: "Unscheduled",
      secondaryTimeLabel: null,
      meta: [entry.booking.notes].filter(Boolean) as string[],
      passengers: entry.guests.map((guest) => ({
        id: guest.id,
        name: guest.name,
        email: guest.email,
        reference: entry.booking.bookingCode,
      })),
    })
  }

  return items.sort(compareItems)
}

export function compareItems(a: ManifestItem, b: ManifestItem): number {
  const time = (a.startsAt ?? "9999").localeCompare(b.startsAt ?? "9999")
  if (time !== 0) return time
  return a.title.localeCompare(b.title)
}

// Distinct travellers across every movement → the passenger manifest list.
export type PassengerSummary = {
  id: string
  name: string
  email?: string | null
  flights: number
  ground: number
  lodging: number
  rentals: number
  movements: number
}

export function buildPassengerManifest(items: ManifestItem[]): PassengerSummary[] {
  const map = new Map<string, PassengerSummary>()
  for (const item of items) {
    for (const p of item.passengers) {
      let row = map.get(p.id)
      if (!row) {
        row = {
          id: p.id,
          name: p.name,
          email: p.email,
          flights: 0,
          ground: 0,
          lodging: 0,
          rentals: 0,
          movements: 0,
        }
        map.set(p.id, row)
      }
      if (!row.email && p.email) row.email = p.email
      row.movements += 1
      if (item.lane === "flights") row.flights += 1
      else if (item.lane === "ground") row.ground += 1
      else if (item.lane === "lodging") row.lodging += 1
      else if (item.lane === "rentals") row.rentals += 1
    }
  }
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name))
}

// ── formatting (local-time-only convention: naive strings, no TZ) ────────────

export function dayKey(value: string | null | undefined): string | null {
  return wallClockDayKey(value)
}

export function formatTime(naive: string): string {
  return formatWallClockTime(naive)
}

export function formatDateTime(naive: string): string {
  return formatWallClockDateTime(naive)
}

export function formatDayLong(date: string): string {
  return formatWallClockDate(date, "weekday-long")
}

function titleCase(value: string): string {
  return value.replace(/\b\w/g, (c) => c.toUpperCase())
}

function nightsBetween(a: string | null, b: string | null): number | null {
  if (!a || !b) return null
  const nights = differenceWallClockDays(a, b)
  return nights == null ? null : Math.max(0, nights)
}

function transferPickupLabel(entry: TransferEntry): string {
  return (
    entry.transfer.pickupLocationName ||
    [entry.transfer.pickupStationCode, entry.transfer.pickupAddressLocality]
      .filter(Boolean)
      .join(" · ") ||
    "Pickup"
  )
}

function transferDropoffLabel(entry: TransferEntry): string {
  return (
    entry.transfer.dropoffLocationName ||
    [entry.transfer.dropoffStationCode, entry.transfer.dropoffAddressLocality]
      .filter(Boolean)
      .join(" · ") ||
    (entry.transfer.transferType === "as_directed" ? "As directed" : "Dropoff")
  )
}

// ── transport-movement model (Console travel CollectionView) ─────────────────
// The Console travel page is a flat, sortable list of transport MOVEMENTS with
// the transport kinds as saved-view tabs. Unlike the manifest lanes above, this
// keeps lodging OFF the page (Lodging gets its own surface) and surfaces every
// segment as a deep-linkable row with a reading-pane detail. Additive layer —
// the lane/manifest exports above are untouched.

export type SegmentKind = "flight" | "ground" | "transfer" | "rental"

export type SegmentStop = {
  label: string
  code: string | null
  detail: string | null
  at: string | null
  /** Curated-venue FK when the stop resolves to a `locations` row (ground
      transfers only) — lets the editor prefill the place picker. */
  locationId?: string | null
}

export type SegmentParty = {
  id: string
  name: string
  email?: string | null
  seat?: string | null
  ticketNumber?: string | null
  role?: string | null
}

export type SegmentDetailRow = {
  key: string
  label: string
  value: string
}

// One row per physical movement, with everything the detail pane needs.
export type TravelSegment = {
  id: string
  kind: SegmentKind
  /** Saved-view bucket: flights | ground | transfer | rentals. */
  tab: "flights" | "ground" | "transfer" | "rentals"
  typeLabel: string
  /**
   * Raw backend record id the edit / delete actions key on:
   * flight → flight_reservations.id, transfer → ground_transfers.id,
   * rental → rental_vehicles.id, standalone booking → travel_bookings.id.
   * (A flight row collapses every segment of one reservation, so this is the
   * reservation it belongs to — editing replaces ALL of its segments.)
   */
  recordId: string
  /** True for the unscheduled standalone-booking rows (not editable here). */
  isStandalone?: boolean
  direction: "arriving" | "departing" | null
  startsAt: string | null
  endsAt: string | null
  dayKey: string | null
  /** Concise route, e.g. "LHR → JFK" or "Hotel → Airport". */
  routeLabel: string
  from: SegmentStop
  to: SegmentStop | null
  /** Carrier + number, vendor, etc. */
  operator: string | null
  serviceLabel: string | null
  confirmation: string | null
  notes: string | null
  /** Status derived from time (scheduled / completed). */
  status: "scheduled" | "completed" | "unscheduled"
  /** Organizer-set operational status (flights + ground): scheduled | delayed |
      departed | arrived | cancelled. Null for kinds without one (rentals, etc.). */
  opStatus?: string | null
  passengers: SegmentParty[]
  /** Extra key/value rows surfaced in the detail pane. */
  detailRows: SegmentDetailRow[]
}

const SEGMENT_TYPE_LABEL: Record<string, string> = {
  arriving: "Arriving flight",
  departing: "Departing flight",
}

// Flatten the event-travel response into transport movements (no lodging).
// Mirrors buildManifestItems' flight-collapsing, but keeps each segment whole
// with the operational detail the reading pane shows.
export function buildSegments(data: EventTravelResponse | undefined): TravelSegment[] {
  if (!data?.success) return []
  const out: TravelSegment[] = []

  // Flights — one row per segment; identical legs across reservations collapse.
  const flightGroups = new Map<string, TravelSegment>()
  for (const entry of data.pnrs ?? []) {
    const segments = [...entry.segments].sort((a, b) => a.position - b.position)
    const dir = (entry.reservation.direction ?? "").toLowerCase()
    const direction = dir === "arriving" ? "arriving" : dir === "departing" ? "departing" : null
    for (const segment of segments) {
      const startsAt = segment.departureAt ?? null
      const endsAt = segment.arrivalAt ?? startsAt
      const seatByGuest = new Map<string, string>()
      for (const seat of segment.seats) {
        if (!seatByGuest.has(seat.guest.id)) seatByGuest.set(seat.guest.id, seat.seat)
      }
      const flightNumber = [segment.carrierIataCode, segment.flightNumber].filter(Boolean).join(" ")
      const passengers: SegmentParty[] = entry.passengers.map((p) => ({
        id: p.guest.id,
        name: p.guest.name,
        email: p.guest.email,
        seat: seatByGuest.get(p.guest.id) ?? null,
        ticketNumber: p.ticketNumber,
      }))
      const groupKey = [
        entry.reservation.direction ?? "",
        `${segment.carrierIataCode ?? ""}${segment.flightNumber ?? ""}@${segment.departureAirportCode ?? ""}-${segment.arrivalAirportCode ?? ""}@${segment.departureAt ?? ""}>${segment.arrivalAt ?? ""}`,
      ].join("::")

      const existing = flightGroups.get(groupKey)
      if (existing) {
        const seen = new Set(existing.passengers.map((p) => p.id))
        for (const p of passengers) {
          if (!seen.has(p.id)) {
            existing.passengers.push(p)
            seen.add(p.id)
          }
        }
        continue
      }

      const routeLabel =
        segment.departureAirportCode && segment.arrivalAirportCode
          ? `${segment.departureAirportCode} → ${segment.arrivalAirportCode}`
          : "Flight movement"

      flightGroups.set(groupKey, {
        // Clean URL id: YYYYMMDD-<airline><flight> (e.g. 20270315-ua1234).
        id: flightSegmentSlug(segment) ?? `flight:${groupKey}`,
        kind: "flight",
        tab: "flights",
        typeLabel: direction ? SEGMENT_TYPE_LABEL[direction] : "Flight",
        recordId: entry.reservation.id,
        direction,
        startsAt,
        endsAt,
        dayKey: dayKey(startsAt),
        routeLabel,
        from: {
          label: segment.departureCity ?? segment.departureAirportCode,
          code: segment.departureAirportCode,
          detail: segment.departureCity,
          at: startsAt,
        },
        to: {
          label: segment.arrivalCity ?? segment.arrivalAirportCode,
          code: segment.arrivalAirportCode,
          detail: segment.arrivalCity,
          at: endsAt,
        },
        operator: flightNumber || entry.reservation.airlineName,
        serviceLabel: entry.reservation.airlineName,
        confirmation: entry.reservation.pnr,
        notes: null,
        status: deriveStatus(endsAt ?? startsAt),
        opStatus: entry.reservation.status ?? "scheduled",
        passengers,
        detailRows: [
          ["Airline", entry.reservation.airlineName],
          ["Flight", flightNumber || null],
          ["Direction", direction ? titleCase(direction) : null],
        ]
          .filter((r): r is [string, string] => Array.isArray(r) && !!r[1])
          .map(([key, value]) => ({ key, label: key, value })),
      })
    }
  }
  out.push(...flightGroups.values())

  // Ground transfers + point-to-point.
  for (const entry of data.transfers ?? []) {
    const pickup = transferPickupLabel(entry)
    const dropoff = transferDropoffLabel(entry)
    const startsAt = entry.transfer.pickupAt
    const endsAt = entry.transfer.dropoffAt
    out.push({
      id: `transfer:${entry.transfer.id}`,
      kind: "transfer",
      tab: "ground",
      typeLabel: transferTypeLabel(entry.transfer.transferType),
      recordId: entry.transfer.id,
      direction: null,
      startsAt,
      endsAt,
      dayKey: dayKey(startsAt),
      routeLabel: `${pickup} → ${dropoff}`,
      from: {
        label: pickup,
        code: entry.transfer.pickupStationCode,
        detail: entry.transfer.pickupAddressLocality,
        at: startsAt,
        locationId: entry.transfer.pickupLocationId,
      },
      to: {
        label: dropoff,
        code: entry.transfer.dropoffStationCode,
        detail: entry.transfer.dropoffAddressLocality,
        at: endsAt,
        locationId: entry.transfer.dropoffLocationId,
      },
      operator: entry.transfer.vendorName,
      serviceLabel: titleCase(entry.transfer.mode.replaceAll("_", " ")),
      confirmation: entry.transfer.confirmationNumber,
      notes: entry.transfer.notes,
      status: deriveStatus(endsAt ?? startsAt),
      opStatus: entry.transfer.status ?? "scheduled",
      passengers: entry.passengers.map((p) => ({
        id: p.guest.id,
        name: p.guest.name,
        email: p.guest.email,
        seat: p.seat,
        ticketNumber: p.ticketNumber,
      })),
      detailRows: [
        ["Vendor", entry.transfer.vendorName],
        ["Mode", titleCase(entry.transfer.mode.replaceAll("_", " "))],
        ["Service", entry.transfer.serviceNumber],
        ["Class", entry.transfer.serviceClass],
        ["Confirmation", entry.transfer.confirmationNumber],
        ["Contact", entry.transfer.vendorContact],
        ["Platform (pickup)", entry.transfer.pickupPlatform],
        ["Platform (dropoff)", entry.transfer.dropoffPlatform],
        entry.transfer.passengerCount != null
          ? ["Passengers", String(entry.transfer.passengerCount)]
          : null,
        entry.transfer.luggageCount != null
          ? ["Luggage", `${entry.transfer.luggageCount} bag${entry.transfer.luggageCount === 1 ? "" : "s"}`]
          : null,
      ]
        .filter((r): r is [string, string] => Array.isArray(r) && !!r[1])
        .map(([key, value]) => ({ key, label: key, value })),
    })
  }

  // Rentals.
  for (const entry of data.rentals ?? []) {
    const startsAt = entry.rental.pickupAt
    const endsAt = entry.rental.dropoffAt
    const vehicle =
      [entry.rental.vehicleMake, entry.rental.vehicleModel].filter(Boolean).join(" ") ||
      entry.rental.vehicleDescription ||
      entry.rental.vehicleClass
    const pickup = entry.rental.pickupLocationName ?? entry.rental.pickupAddressLocality ?? "Pickup"
    const dropoff = entry.rental.dropoffLocationName ?? entry.rental.dropoffAddressLocality ?? "Return"
    out.push({
      id: `rental:${entry.rental.id}`,
      kind: "rental",
      tab: "rentals",
      typeLabel: "Car rental",
      recordId: entry.rental.id,
      direction: null,
      startsAt,
      endsAt,
      dayKey: dayKey(startsAt),
      routeLabel: dropoff && dropoff !== pickup ? `${pickup} → ${dropoff}` : pickup,
      from: {
        label: pickup,
        code: null,
        detail: entry.rental.pickupAddressLocality,
        at: startsAt,
      },
      to: {
        label: dropoff,
        code: null,
        detail: entry.rental.dropoffAddressLocality,
        at: endsAt,
      },
      operator: entry.rental.vendorName,
      serviceLabel: vehicle || null,
      confirmation: entry.rental.confirmationNumber,
      notes: entry.rental.notes,
      status: deriveStatus(endsAt ?? startsAt),
      passengers: entry.occupants.map((o) => ({
        id: o.guest.id,
        name: o.guest.name,
        email: o.guest.email,
        role: ROLE_LABEL[o.role] ?? o.role,
      })),
      detailRows: [
        ["Vendor", entry.rental.vendorName],
        ["Vehicle", vehicle || null],
        ["Class", entry.rental.vehicleClass],
        ["Plate", entry.rental.licensePlate],
        ["Confirmation", entry.rental.confirmationNumber],
      ]
        .filter((r): r is [string, string] => Array.isArray(r) && !!r[1])
        .map(([key, value]) => ({ key, label: key, value })),
    })
  }

  // Standalone bookings → unscheduled ground movements.
  for (const entry of data.standaloneBookings ?? []) {
    out.push({
      id: `booking:${entry.booking.id}`,
      kind: "ground",
      tab: "ground",
      typeLabel: "Unscheduled booking",
      recordId: entry.booking.id,
      isStandalone: true,
      direction: null,
      startsAt: null,
      endsAt: null,
      dayKey: null,
      routeLabel: "Unscheduled booking",
      from: { label: "—", code: null, detail: null, at: null },
      to: null,
      operator: entry.booking.agencyName,
      serviceLabel: SOURCE_LABEL[entry.booking.bookingSource] ?? titleCase(entry.booking.bookingSource),
      confirmation: entry.booking.bookingCode,
      notes: entry.booking.notes,
      status: "unscheduled",
      passengers: entry.guests.map((g) => ({
        id: g.id,
        name: g.name,
        email: g.email,
      })),
      detailRows: [
        ["Source", SOURCE_LABEL[entry.booking.bookingSource] ?? titleCase(entry.booking.bookingSource)],
        ["Agency", entry.booking.agencyName],
        ["Code", entry.booking.bookingCode],
      ]
        .filter((r): r is [string, string] => Array.isArray(r) && !!r[1])
        .map(([key, value]) => ({ key, label: key, value })),
    })
  }

  return out.sort(compareSegments)
}

export function compareSegments(a: TravelSegment, b: TravelSegment): number {
  const time = (a.startsAt ?? "9999").localeCompare(b.startsAt ?? "9999")
  if (time !== 0) return time
  return a.routeLabel.localeCompare(b.routeLabel)
}

function deriveStatus(at: string | null): "scheduled" | "completed" | "unscheduled" {
  if (!at) return "unscheduled"
  const comparison = compareWallClockToNow(at)
  return comparison != null && comparison < 0 ? "completed" : "scheduled"
}

// Single primary traveler label for the list column (collapses long lists).
export function travelersLabel(parties: SegmentParty[]): string {
  if (parties.length === 0) return "No travelers"
  if (parties.length === 1) return parties[0].name
  return `${parties[0].name} +${parties.length - 1}`
}

// ── grid model (Travel GRID view: day rows × type lanes) ─────────────────────
// The grid mirrors apps/admin's calendar-style travel board, adapted to the
// Console's day-grouped shape: each DAY is a row, each TYPE is a lane. It reuses
// buildSegments for every transport movement (so flight-collapsing, route
// labels, status, passengers, and the deep-linkable detail all match the table
// 1:1) and additively layers LODGING spans straight off the same event-travel
// response. Lodging has its own feature surface, so its grid cards are
// display-only; transport cards carry the segment.id the table row opens.

export type TravelGridLane =
  | "arriving"
  | "departing"
  | "ground"
  | "lodging"
  | "rentals"
  | "unscheduled"

export const TRAVEL_GRID_LANE_ORDER: TravelGridLane[] = [
  "arriving",
  "departing",
  "ground",
  "lodging",
  "rentals",
  "unscheduled",
]

export const TRAVEL_GRID_LANE_LABEL: Record<TravelGridLane, string> = {
  arriving: "Arriving",
  departing: "Departing",
  ground: "Ground",
  lodging: "Lodging",
  rentals: "Rentals",
  unscheduled: "Unscheduled",
}

// A lodging span card — derived from data.rooms, NOT a TravelSegment (the travel
// table + detail are transport-only). Display-only in the grid.
export type LodgingCard = {
  id: string
  title: string
  subtitle: string | null
  dayKey: string | null
  checkInAt: string | null
  checkOutAt: string | null
  /** "3:00pm" check-in time, for the compact card. */
  primaryTimeLabel: string
  /** "Out Jun 18 · 11:00am", for the compact card. */
  secondaryTimeLabel: string | null
  nights: number | null
  confirmation: string | null
  occupants: SegmentParty[]
}

// One card in the grid: either a clickable transport segment (opens the same
// detail as the table row) or a display-only lodging span.
export type TravelGridCard =
  | { kind: "segment"; lane: TravelGridLane; dayKey: string | null; sortAt: string; segment: TravelSegment }
  | { kind: "lodging"; lane: "lodging"; dayKey: string | null; sortAt: string; lodging: LodgingCard }

export type TravelGridLaneGroup = {
  lane: TravelGridLane
  label: string
  cards: TravelGridCard[]
}

export type TravelGridDay = {
  /** dayKey ("2026-06-15") or null for the Unscheduled day row. */
  dayKey: string | null
  /** Pre-formatted long label, or "Unscheduled" for the null day. */
  label: string
  total: number
  lanes: TravelGridLaneGroup[]
}

function segmentLane(segment: TravelSegment): TravelGridLane {
  if (segment.isStandalone || !segment.dayKey) return "unscheduled"
  if (segment.kind === "flight") return segment.direction === "arriving" ? "arriving" : "departing"
  if (segment.kind === "rental") return "rentals"
  return "ground"
}

// Build the day-grouped, lane-bucketed grid. `segments` come from buildSegments
// (already deep-linkable + collapsed); lodging comes off the raw response so the
// grid stays a superset of the table without duplicating any parsing.
export function buildTravelGrid(
  segments: TravelSegment[],
  data: EventTravelResponse | undefined,
): TravelGridDay[] {
  const cards: TravelGridCard[] = []

  for (const segment of segments) {
    cards.push({
      kind: "segment",
      lane: segmentLane(segment),
      dayKey: segment.dayKey,
      sortAt: segment.startsAt ?? "9999",
      segment,
    })
  }

  for (const entry of data?.success ? data.rooms ?? [] : []) {
    const checkInAt = entry.reservation.checkInAt
    const checkOutAt = entry.reservation.checkOutAt
    const nights = nightsBetween(checkInAt, checkOutAt)
    cards.push({
      kind: "lodging",
      lane: "lodging",
      dayKey: dayKey(checkInAt),
      sortAt: checkInAt ?? "9999",
      lodging: {
        id: `lodging:${entry.reservation.id}`,
        title: entry.reservation.hotelName,
        subtitle:
          [entry.reservation.addressLocality, entry.reservation.addressRegion]
            .filter(Boolean)
            .join(", ") || entry.reservation.roomType,
        dayKey: dayKey(checkInAt),
        checkInAt,
        checkOutAt,
        primaryTimeLabel: checkInAt ? `In ${formatTime(checkInAt)}` : "Time missing",
        secondaryTimeLabel: checkOutAt ? `Out ${formatDateTime(checkOutAt)}` : null,
        nights,
        confirmation: entry.reservation.confirmationNumber,
        occupants: entry.occupants.map((o) => ({
          id: o.guest.id,
          name: o.guest.name,
          email: o.guest.email,
        })),
      },
    })
  }

  // Bucket by day, then by lane (preserving lane order). Null-day cards collapse
  // into a single trailing "Unscheduled" day row.
  const byDay = new Map<string | null, TravelGridCard[]>()
  for (const card of cards) {
    const key = card.dayKey ?? null
    const list = byDay.get(key)
    if (list) list.push(card)
    else byDay.set(key, [card])
  }

  const scheduledDays = Array.from(byDay.keys())
    .filter((k): k is string => k != null)
    .sort((a, b) => a.localeCompare(b))
  const orderedKeys: Array<string | null> = [...scheduledDays]
  if (byDay.has(null)) orderedKeys.push(null)

  return orderedKeys.map((key) => {
    const dayCards = (byDay.get(key) ?? []).slice().sort(compareGridCards)
    const lanes: TravelGridLaneGroup[] = TRAVEL_GRID_LANE_ORDER.map((lane) => ({
      lane,
      label: TRAVEL_GRID_LANE_LABEL[lane],
      cards: dayCards.filter((c) => c.lane === lane),
    })).filter((g) => g.cards.length > 0)
    return {
      dayKey: key,
      label: key ? formatDayLong(key) : "Unscheduled",
      total: dayCards.length,
      lanes,
    }
  })
}

function compareGridCards(a: TravelGridCard, b: TravelGridCard): number {
  const t = a.sortAt.localeCompare(b.sortAt)
  if (t !== 0) return t
  const at = a.kind === "segment" ? a.segment.routeLabel : a.lodging.title
  const bt = b.kind === "segment" ? b.segment.routeLabel : b.lodging.title
  return at.localeCompare(bt)
}

// ── write layer (create / update / delete) ───────────────────────────────────
// All travel writes go through the bespoke admin actions (POST /api/v1/admin/X)
// rather than raw table CRUD — they auto-find/create the booking envelope,
// resolve affected userIds via guests.linkedUserId, snapshot reference
// data, and stamp cohort keys. Times are NAIVE local strings (event-local wall
// clock, no TZ) per the app's local-time-only convention.

export type BookingSource = "agent" | "direct" | "self"
export type FlightDirection = "arriving" | "departing"
export type GroundDirection = "arriving" | "departing" | "local"
export type GroundMode =
  | "shuttle"
  | "private_car"
  | "rideshare"
  | "taxi"
  | "train"
  | "bus"
  | "ferry"
  | "other"
export type TransferType = "scheduled" | "as_directed"

/** One leg of a flight itinerary, as the add/update-flight action expects. */
export type FlightSegmentInput = {
  carrierIataCode: string
  flightNumber: string
  departureAirportCode: string
  departureCity?: string
  departureAt: string
  arrivalAirportCode: string
  arrivalCity?: string
  arrivalAt: string
  bookingClass?: string
}

export type AddFlightInput = {
  guestIds: string[]
  bookingSource?: BookingSource
  agencyName?: string
  bookingCode?: string
  direction: FlightDirection
  pnr?: string
  airlineName: string
  airlineIataCode: string
  segments: FlightSegmentInput[]
}

export type AddGroundInput = {
  guestIds: string[]
  direction: GroundDirection
  mode: GroundMode
  transferType: TransferType
  vendorName: string
  confirmationNumber?: string
  serviceNumber?: string
  serviceClass?: string
  pickupAt: string
  pickupLocationId?: string | null
  pickupLocationName?: string
  pickupStationCode?: string
  pickupPlatform?: string
  dropoffAt: string
  dropoffLocationId?: string | null
  dropoffLocationName?: string
  dropoffStationCode?: string
  dropoffPlatform?: string
  passengerCount?: number
  luggageCount?: number
  transferNotes?: string
}

export type AddRentalInput = {
  guestIds: string[]
  vendorName: string
  confirmationNumber?: string
  vehicleClass?: string
  vehicleDescription?: string
  pickupAt: string
  pickupLocationName?: string
  dropoffAt: string
  dropoffLocationName?: string
  rentalNotes?: string
}

type WriteResult = { success: boolean } & Record<string, unknown>

// ── operational status (flights + ground) ────────────────────────────────────
export const TRAVEL_STATUSES = ["scheduled", "delayed", "departed", "arrived", "cancelled"] as const
export type TravelStatus = (typeof TRAVEL_STATUSES)[number]

export const TRAVEL_STATUS_LABEL: Record<string, string> = {
  scheduled: "Scheduled",
  delayed: "Delayed",
  departed: "Departed",
  arrived: "Arrived",
  cancelled: "Cancelled",
}

// Badge tone per status (console plum/semantic palette).
export const TRAVEL_STATUS_TONE: Record<string, string> = {
  scheduled: "bg-plum-100 text-plum-700 dark:bg-white/10 dark:text-plum-200/80",
  delayed: "bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-300",
  departed: "bg-sky-100 text-sky-800 dark:bg-sky-400/15 dark:text-sky-300",
  arrived: "bg-emerald-100 text-emerald-800 dark:bg-emerald-400/15 dark:text-emerald-300",
  cancelled: "bg-rose-100 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300",
}

export function travelStatusLabel(s: string | null | undefined): string {
  return TRAVEL_STATUS_LABEL[s ?? "scheduled"] ?? "Scheduled"
}

// Flip a flight reservation / ground transfer status without re-submitting the
// whole movement. Caller invalidates eventTravelKey to refresh.
export async function setTravelStatus(input: {
  kind: "flight" | "ground"
  id: string
  status: TravelStatus
}): Promise<WriteResult> {
  return api<WriteResult>("/api/v1/admin/travel-set-status", { method: "POST", body: input })
}

// Strip empty-string / undefined optionals so the action never sees a "".
function clean<T extends Record<string, unknown>>(obj: T): T {
  const out = {} as Record<string, unknown>
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null) continue
    if (typeof v === "string" && v.trim() === "") continue
    out[k] = v
  }
  return out as T
}

// ── Flights ──────────────────────────────────────────────────────────────────

export function addTravelFlight(input: AddFlightInput) {
  return api<WriteResult>("/api/v1/admin/travel-add-flight", {
    method: "POST",
    body: clean({
      guestIds: input.guestIds,
      bookingSource: input.bookingSource ?? "agent",
      agencyName: input.agencyName,
      bookingCode: input.bookingCode,
      direction: input.direction,
      pnr: input.pnr,
      airlineName: input.airlineName,
      airlineIataCode: input.airlineIataCode,
      segments: input.segments.map(cleanSegment),
    }),
  })
}

export function updateTravelFlight(reservationId: string, input: Omit<AddFlightInput, "guestIds" | "bookingSource" | "agencyName" | "bookingCode">) {
  return api<WriteResult>("/api/v1/admin/travel-update-flight", {
    method: "POST",
    body: clean({
      reservationId,
      direction: input.direction,
      pnr: input.pnr,
      airlineName: input.airlineName,
      airlineIataCode: input.airlineIataCode,
      segments: input.segments.map(cleanSegment),
    }),
  })
}

export function deleteTravelFlight(reservationId: string) {
  return api<WriteResult>("/api/v1/admin/travel-delete-flight", {
    method: "POST",
    body: { reservationId },
  })
}

function cleanSegment(s: FlightSegmentInput) {
  return clean({
    carrierIataCode: s.carrierIataCode.toUpperCase(),
    flightNumber: s.flightNumber.trim(),
    departureAirportCode: s.departureAirportCode.toUpperCase(),
    departureCity: s.departureCity,
    departureAt: s.departureAt,
    arrivalAirportCode: s.arrivalAirportCode.toUpperCase(),
    arrivalCity: s.arrivalCity,
    arrivalAt: s.arrivalAt,
    bookingClass: s.bookingClass,
  })
}

// ── Ground transfers ─────────────────────────────────────────────────────────

export function addTravelGround(input: AddGroundInput) {
  return api<WriteResult>("/api/v1/admin/travel-add-ground", {
    method: "POST",
    body: clean({ ...input, guestIds: input.guestIds }),
  })
}

// The update action names the transfer-notes field `notes` (not `transferNotes`).
export function updateTravelGround(transferId: string, input: Omit<AddGroundInput, "guestIds">) {
  const { transferNotes, ...rest } = input
  return api<WriteResult>("/api/v1/admin/travel-update-ground", {
    method: "POST",
    body: clean({ transferId, ...rest, notes: transferNotes }),
  })
}

export function deleteTravelGround(transferId: string) {
  return api<WriteResult>("/api/v1/admin/travel-delete-ground", {
    method: "POST",
    body: { transferId },
  })
}

// ── Rentals ──────────────────────────────────────────────────────────────────

export function addTravelRental(input: AddRentalInput) {
  return api<WriteResult>("/api/v1/admin/travel-add-rental", {
    method: "POST",
    body: clean({ ...input, guestIds: input.guestIds }),
  })
}

// The update action names the rental-notes field `notes` (not `rentalNotes`).
export function updateTravelRental(rentalId: string, input: Omit<AddRentalInput, "guestIds">) {
  const { rentalNotes, ...rest } = input
  return api<WriteResult>("/api/v1/admin/travel-update-rental", {
    method: "POST",
    body: clean({ rentalId, ...rest, notes: rentalNotes }),
  })
}

export function deleteTravelRental(rentalId: string) {
  return api<WriteResult>("/api/v1/admin/travel-delete-rental", {
    method: "POST",
    body: { rentalId },
  })
}
